import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import aiModelService from "services/aiModels.js";
import logger from "utils/logger";

export default function CardAiModelForm({ model, isOpen, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    api_identifier: '',
    provider: '',
    description: '',
    active: true,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      if (model) {
        setFormData({
          name: model.name || '',
          api_identifier: model.api_identifier || '',
          provider: model.provider || '',
          description: model.description || '',
          active: model.active !== undefined ? model.active : true,
        });
      } else {
        setFormData({
          name: '',
          api_identifier: '',
          provider: '',
          description: '',
          active: true,
        });
      }
      setErrors({});
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [model, isOpen]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onCancel]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.api_identifier.trim()) {
      newErrors.api_identifier = 'El identificador API es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let result;
      if (model) {
        result = await aiModelService.updateModel(model.id, formData);
      } else {
        result = await aiModelService.createModel(formData);
      }

      if (onSave) {
        onSave(result);
      }
    } catch (error) {
      logger.error('Error saving AI model:', error);

      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({
          general: 'Error al guardar el modelo. Por favor, inténtalo de nuevo.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      api_identifier: '',
      provider: '',
      description: '',
      active: true,
    });
    setErrors({});
    onCancel();
  };

  if (!isOpen) return null;

  const spinnerStyle = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  return createPortal(
    <>
      <style>{spinnerStyle}</style>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto',
            zIndex: 100000,
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem 1.5rem 1rem',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: '#374151' }}>
              {model ? 'Editar Modelo de IA' : 'Nuevo Modelo de IA'}
            </h3>
            <button
              onClick={handleCancel}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '0.25rem'
              }}
            >
              ×
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
            {errors.general && (
              <div style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                borderRadius: '0.375rem'
              }}>
                {errors.general}
              </div>
            )}

            {/* Name */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                color: '#374151',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                marginBottom: '0.5rem'
              }}>
                Nombre *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Ej: Claude Opus 4"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: errors.name ? '1px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  backgroundColor: loading ? '#f3f4f6' : 'white'
                }}
              />
              {errors.name && (
                <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {errors.name}
                </p>
              )}
            </div>

            {/* API Identifier */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                color: '#374151',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                marginBottom: '0.5rem'
              }}>
                Identificador API *
              </label>
              <input
                type="text"
                name="api_identifier"
                value={formData.api_identifier}
                onChange={handleInputChange}
                placeholder="Ej: claude-opus-4-6"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: errors.api_identifier ? '1px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  fontFamily: 'monospace',
                  backgroundColor: loading ? '#f3f4f6' : 'white'
                }}
              />
              {errors.api_identifier && (
                <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {errors.api_identifier}
                </p>
              )}
              <p style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                Identificador único utilizado en la API del proveedor
              </p>
            </div>

            {/* Provider */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                color: '#374151',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                marginBottom: '0.5rem'
              }}>
                Proveedor
              </label>
              <input
                type="text"
                name="provider"
                value={formData.provider}
                onChange={handleInputChange}
                placeholder="Ej: Anthropic, OpenAI, Google"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: errors.provider ? '1px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  backgroundColor: loading ? '#f3f4f6' : 'white'
                }}
              />
              {errors.provider && (
                <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {errors.provider}
                </p>
              )}
            </div>

            {/* Description */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                color: '#374151',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                marginBottom: '0.5rem'
              }}>
                Descripción
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Descripción del modelo de IA"
                rows="3"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: errors.description ? '1px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  backgroundColor: loading ? '#f3f4f6' : 'white',
                  resize: 'vertical'
                }}
              />
              {errors.description && (
                <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {errors.description}
                </p>
              )}
            </div>

            {/* Active */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}>
                <button
                  type="button"
                  onClick={() => !loading && setFormData(prev => ({ ...prev, active: !prev.active }))}
                  disabled={loading}
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    width: '2.75rem',
                    height: '1.5rem',
                    borderRadius: '9999px',
                    border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    backgroundColor: formData.active ? '#10b981' : '#d1d5db',
                    transition: 'background-color 0.2s',
                    flexShrink: 0,
                    padding: 0,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      width: '1.25rem',
                      height: '1.25rem',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      transform: formData.active ? 'translateX(1.375rem)' : 'translateX(0.125rem)',
                      transition: 'transform 0.2s',
                    }}
                  />
                </button>
                <span style={{ color: '#374151', fontSize: '0.875rem', fontWeight: 'bold' }}>
                  Activo
                </span>
                <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                  {formData.active ? 'El modelo está disponible para asignación' : 'El modelo está desactivado'}
                </span>
              </label>
            </div>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              paddingTop: '1rem',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  textTransform: 'uppercase',
                  opacity: loading ? 0.5 : 1
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: loading ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {loading ? (
                  <>
                    <span style={{
                      display: 'inline-block',
                      width: '1rem',
                      height: '1rem',
                      border: '2px solid #ffffff',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></span>
                    Guardando...
                  </>
                ) : (
                  <>
                    {model ? 'Actualizar' : 'Crear'} Modelo
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
}

CardAiModelForm.propTypes = {
  model: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
